import { useMemo } from 'react';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import { PORTFOLIO_DAYS_BY_PERIOD } from 'src/components/PortfolioPage/consts';
import useAccountTradingSnapshots from 'src/hooks/query/snapshots/account/useAccountTradingSnapshots';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import fillMissingPortfolioSnapshots, {
	PortfolioSnapshotsFillStrategy,
} from 'src/components/PortfolioPage/utils/fillMissingPortfolioSnapshots';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';

interface Params {
	period: PortfolioPeriod;
	maxSnapshots?: number;
	fillStrategy: PortfolioSnapshotsFillStrategy;
}

/**
 * Fetches account trading snapshot data and returns them normalized:
 * - Sorted chronologically (oldest -> newest).
 * - Filled with missing snapshots based on the fill strategy.
 * - Sliced to the maximum number of snapshots if provided.
 */
export default function usePortfolioTradingSnapshots({
	period: periodFromParams,
	fillStrategy,
	maxSnapshots: maxSnapshotsFromParams,
}: Params) {
	const currentAccountPubKey = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()?.pubKey?.toString()
	);

	const { data, ...rest } = useAccountTradingSnapshots({
		userAccount: currentAccountPubKey,
		days: PORTFOLIO_DAYS_BY_PERIOD[periodFromParams],
	});

	const period = useLatchedValue({
		value: periodFromParams,
		isLatched: rest.isPlaceholderData,
	});

	const maxSnapshots = useLatchedValue({
		value: maxSnapshotsFromParams,
		isLatched: rest.isPlaceholderData,
	});

	const normalizedData = useMemo(() => {
		if (!data?.data.snapshots) {
			return;
		}

		const { data: tradeData } = data;

		const snapshots = tradeData.snapshots;

		// Normalize sort to chronological (oldest -> newest).
		const sortedSnapshots = [...snapshots].sort((a, b) => a.ts - b.ts);

		const filledSnapshots = fillMissingPortfolioSnapshots({
			period,
			snapshots: sortedSnapshots,
			strategy: fillStrategy,
		});

		const slicedSnapshots = filledSnapshots.slice(
			maxSnapshots ? -maxSnapshots : undefined
		);

		return {
			...tradeData,
			snapshots: slicedSnapshots,
		};
	}, [data, period, maxSnapshots, fillStrategy]);

	return {
		data: normalizedData,
		...rest,
	};
}
